import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Stack } from "@mui/material";
import useQueryParams from "../hooks/useQueryParams ";
import axiosInstance from "../utils/axiosConfig";
import { notifyToast } from "../utils/notify";
import { useSettings } from "../hooks/useSettings";
import useDebounce from "../hooks/useDebounce";
import DataTable from "./DataTable";
import SearchActionComponent from "./reusableComponents/SearchActionComponent";
import CommonTableHead from "./CommonTableHead";
import SwipeableDrawerComponent from "./SwipeableDrawerComponent ";
import CustomFilter from "./reusableComponents/Filter/CustomFilter";
import InsuranceDetailModal from "./InsuranceDetailModal";
import { customeDateHandler } from "../const/commonfilterobject";
import RideStatus from "./RideStatus";
import { PrimaryButton } from "./button";
import FilteredResults from "./FilteredResults";

const Insurance = () => {
  const { searchParams, setSearchParams, setQuery, removeQuery, getQuery } = useQueryParams();
  const { dateformat, timezone, formatDate, timeFormat } = useSettings();

  const [loading, setLoading] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  const searchDebounce = useDebounce(search, 500);

  const page = parseInt(searchParams.get("page"), 10) || 0;
  const limit = parseInt(searchParams.get("limit"), 10) || 25;
  const [paginationModel, setPaginationModel] = useState({
    page: page || 0,
    pageSize: limit || 25,
  });

  const statusFilter = getQuery("status") || "";
  const setStatusFilter = (val) => {
    const params = new URLSearchParams(searchParams);
    if (val) {
      params.set("status", val);
    } else {
      params.delete("status");
    }
    if (val !== "DELAYED") {
      params.delete("delay_claim_credited");
    }
    setSearchParams(params, { replace: true });
  };

  const claimStatusFilter = getQuery("delay_claim_credited") || "";
  const setClaimStatusFilter = (val) => {
    const params = new URLSearchParams(searchParams);
    if (val) {
      params.set("delay_claim_credited", val);
    } else {
      params.delete("delay_claim_credited");
    }
    setSearchParams(params, { replace: true });
  };
  const [date, setDate] = useState({ startDate: "", endDate: "" });
  const [showFilter, setShowFilter] = useState({ right: false });

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [createdDateOption, setCreatedDateOption] = useState("");

  const [retryLoadingIds, setRetryLoadingIds] = useState(new Set());

  const buildQuery = (download = false) => {
    const query = [];
    if (searchDebounce) query.push(`search=${searchDebounce}`);
    if (statusFilter) {
      query.push(`status=${statusFilter}`);
    }
    if (claimStatusFilter) query.push(`delay_claim_credited=${claimStatusFilter}`);
    if (createdDateOption) query.push(`created_on=${createdDateOption}`);
    if (date.startDate) {
      query.push(`from_date=${date.startDate.format("YYYY-MM-DD")}`);
    }
    if (date.endDate) {
      query.push(`to_date=${date.endDate.format("YYYY-MM-DD")}`);
    }
    if (download) query.push(`is_download=true`);
    return query.join("&");
  };

  const fetchPolicies = async () => {
    setLoading(true);
    const query = buildQuery();
    const url = `/crm/insurance/policies/?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}${query ? `&${query}` : ""}`;
    try {
      const response = await axiosInstance.get(url);
      setPolicies(response?.data?.data?.results || []);
      setTotalCount(response?.data?.data?.count || 0);
    } catch (error) {
      notifyToast.error("Error", error?.response?.data?.message || "Failed to fetch policies");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const query = buildQuery(true);
    const url = `/crm/insurance/policies/?${query}`;
    
    try {
      const response = await axiosInstance.get(url);
      if (response) {
        notifyToast.success(
          "Success",
          "file has been successfully sent via Email. Check your Email account",
          {
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      notifyToast.error("Error", error?.response?.data?.message || "Failed to trigger download");
    }
  };

  const handleRetry = async (policyId) => {
    setRetryLoadingIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(policyId);
      return newSet;
    });

    try {
      const response = await axiosInstance.post(`/crm/insurance/policies/${policyId}/retry/`, {});
      notifyToast.success("Success", response?.data?.message || "Retry initiated successfully.");
      fetchPolicies();
    } catch (error) {
      notifyToast.error("Error", error?.response?.data?.message || "Retry failed.");
    } finally {
      setRetryLoadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(policyId);
        return newSet;
      });
    }
  };

  const toggleFilterDrawer = (anchor, open) => (event) => {
    if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setShowFilter({ ...showFilter, [anchor]: open });
  };

  const openDetailModal = (id) => {
    setSelectedPolicy(id);
    setDetailOpen(true);
  };

  useEffect(() => {
    fetchPolicies();
  }, [searchDebounce, paginationModel.page, paginationModel.pageSize, statusFilter, date.startDate, date.endDate, createdDateOption, claimStatusFilter]);



  const filterOptions = [
    {
      summryText: "Insurance Status",
      filterName: "status",
      state: statusFilter,
      setState: setStatusFilter,
      options: [
        { label: "Applied", value: "BOUND" },
        { label: "Failed", value: "FAILED" },
        { label: "Quoted", value: "QUOTED" },
        { label: "Delayed", value: "DELAYED" },
        // { label: "Pending Quote", value: "PENDING_QUOTE" },
        // { label: "Cancelled", value: "CANCELLED" },
        // { label: "Expired", value: "EXPIRED" },
      ],
    },
    {
      summryText: "Created Date",
      filterName: "created_on",
      state: createdDateOption,
      setState: setCreatedDateOption,
      options: [
        { label: "Yesterday", value: "yesterday" },
        { label: "Last Week", value: "last_week" },
        { label: "Last month", value: "last_month" },
        { label: "Last year", value: "last_year" },
      ],
      date: {
        customDateState: date,
        setState: setDate,
        customeDateHandler,
        customDateHeader: "Custom",
        filterName: ["from_date", "to_date"],
        customDateLabel: ["From", "To"],
        customDateName: ["startDate", "endDate"],
      },
    },
    ...(statusFilter === "DELAYED" ? [
      {
        summryText: "Claim Status",
        filterName: "delay_claim_credited",
        state: claimStatusFilter,
        setState: setClaimStatusFilter,
        options: [
          { label: "Claimed", value: "true" },
          { label: "In Progress", value: "false" },
        ],
      }
    ] : [])
  ];

  const columns = [
    {
      field: "booking_id",
      headerName: "Booking ID",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Button
          sx={{
            fontFamily: "Proxima Nova",
            fontSize: "13px",
            letterSpacing: "0.26px",
            textTransform: "capitalize",
            border: "none",
            color: "#043A87"
          }}
          onClick={(e) => {
            e.stopPropagation();
            openDetailModal(params.row.id);
          }}
        >
          {params.value || "--"}
        </Button>
      ),
    },
        {
      field: "quote_id",
      headerName: "Quote ID",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => <Typography sx={{ fontSize: "13px" }}>{params.value || "--"}</Typography>,
    },
    {
      field: "rider.full_name",
      headerName: "Customer Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => <Typography sx={{ fontSize: "13px" }}>{params.row.rider?.full_name || "--"}</Typography>,
    },
    // {
    //   field: "rider.phone",
    //   headerName: "Customer Phone",
    //   flex: 1,
    //   minWidth: 130,
    //   renderCell: (params) => <Typography sx={{ fontSize: "13px" }}>{params.row.rider?.phone || "--"}</Typography>,
    // },
    // {
    //   field: "rider.id",
    //   headerName: "Customer ID",
    //   flex: 1,
    //   minWidth: 100,
    //   renderCell: (params) => <Typography sx={{ fontSize: "13px" }}>{params.row.rider?.id || "--"}</Typography>,
    // },
    {
      field: "driver.full_name",
      headerName: "Driver Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => <Typography sx={{ fontSize: "13px" }}>{params.row.driver?.full_name || "--"}</Typography>,
    },
    // {
    //   field: "driver.phone",
    //   headerName: "Driver Phone",
    //   flex: 1,
    //   minWidth: 130,
    //   renderCell: (params) => <Typography sx={{ fontSize: "13px" }}>{params.row.driver?.phone || "--"}</Typography>,
    // },
    {
      field: "premium_charged_to_rider",
      headerName: "Premium Charged",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => <Typography sx={{ fontSize: "13px" }}>{params.value ? `${params.row.currency || ""} ${params.value}` : "--"}</Typography>,
    },
        {
      field: "ride.status",
      headerName: "Ride Status",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" gap="5px">
          <RideStatus row={params.row.ride || {}} />
          <Typography
            sx={{
              color: "#000",
              fontFamily: "Proxima Nova",
              fontSize: "13px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              letterSpacing: "0.26px",
              textTransform: "capitalize",
            }}
          >
            {params.row.ride?.status ? params.row.ride.status.replace(/_/g, " ").toLowerCase() : "--"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "status",
      headerName: "Insurance Status",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => {
        let color = "#6F747E";
        let bgColor = "rgba(111, 116, 126, 0.10)";
        
        if (params.value === "BOUND") {
          color = "#009D0F";
          bgColor = "rgba(0, 157, 15, 0.09)";
        } else if (params.value === "FAILED") {
          color = "#F1554C";
          bgColor = "rgba(241, 85, 76, 0.08)";
        } else if (params.value === "QUOTED" || params.value === "PENDING_QUOTE") {
          color = "#F98244";
          bgColor = "rgba(249, 130, 68, 0.09)";
        }
        
        return (
          <Typography
            sx={{
              color: color,
              textAlign: "center",
              fontFamily: "Proxima Nova",
              fontSize: "13px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              letterSpacing: "0.26px",
              textTransform: "capitalize",
              borderRadius: "40px",
              padding: "4px 8px",
              background: bgColor,
            }}
          >
            {params.value ? (params.value === "BOUND" ? "applied" : params.value.replace(/_/g, " ").toLowerCase()) : "--"}
          </Typography>
        );
      },
    },
    {
      field: "created_at",
      headerName: `Created At (${dateformat})`,
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        if (!params.value) return "--";
        const d = formatDate(params.value, timezone, dateformat);
        const t = formatDate(params.value, timezone, timeFormat);
        return (
          <Stack direction="row" gap="5px" alignItems="flex-end">
            <Typography sx={{ fontSize: "13px", fontWeight: "500" }}>{d}</Typography>
            <Typography sx={{ fontSize: "10px", color: "#6F747E" }}>{t}</Typography>
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => {
        const canRetry = ["FAILED", "QUOTED", "PENDING_QUOTE"].includes(params.row.status);
        if (!canRetry) return null;
        const isRetrying = retryLoadingIds.has(params.row.id);

        return (
          <PrimaryButton
            disabled={isRetrying}
            sx={{
              opacity: isRetrying ? 0.6 : 1,
              minWidth: "70px",
              py: "4px",
              height:"24px",
              
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRetry(params.row.id);
            }}
          >
            {isRetrying ? "Retrying…" : "Retry"}
          </PrimaryButton>
        );
      },
    }
  ];

  const getColumns = () => {
    if (statusFilter === "DELAYED") {
      const filtered = columns.filter(
        (col) => col.field !== "actions" && col.field !== "ride.status" && col.field !== "status"
      );
      return [
        ...filtered,
        {
          field: "ride.delay_claim_credited",
          headerName: "Claim Status",
          flex: 1,
          minWidth: 150,
          renderCell: (params) => {
            const isCredited = params?.row?.ride?.delay_claim_credited ?? false;
            const text = isCredited ? "claimed" : "in-progress";
            
            let color = "#F98244";
            let bgColor = "rgba(249, 130, 68, 0.09)";

            if (isCredited) {
              color = "#009D0F";
              bgColor = "rgba(0, 157, 15, 0.09)";
            }

            return (
              <Typography
                sx={{
                  color: color,
                  textAlign: "center",
                  fontFamily: "Proxima Nova",
                  fontSize: "13px",
                  fontWeight: 400,
                  lineHeight: "normal",
                  letterSpacing: "0.26px",
                  textTransform: "capitalize",
                  borderRadius: "40px",
                  padding: "4px 8px",
                  background: bgColor,
                }}
              >
                {text}
              </Typography>
            );
          }
        },
        {
          field: "ride.estimated_arrival_time",
          headerName: "Estimate Time",
          flex: 1,
          minWidth: 160,
          renderCell: (params) => {
            const val = params.row.ride?.estimated_arrival_time || params.row.ride?.estimated_time;
            if (!val) return "--";
            const d = formatDate(val, timezone, dateformat);
            const t = formatDate(val, timezone, timeFormat);
            return (
              <Stack direction="row" gap="5px" alignItems="flex-end">
                <Typography sx={{ fontSize: "13px", fontWeight: "500" }}>{d}</Typography>
                <Typography sx={{ fontSize: "10px", color: "#6F747E" }}>{t}</Typography>
              </Stack>
            );
          },
        },
        {
          field: "ride.arrival_time",
          headerName: "Arrival Time",
          flex: 1,
          minWidth: 160,
          renderCell: (params) => {
            const val = params.row.ride?.arrival_time || params.row.ride?.actual_arrival_time || params.row.ride?.arrived_at;
            if (!val) return "--";
            const d = formatDate(val, timezone, dateformat);
            const t = formatDate(val, timezone, timeFormat);
            return (
              <Stack direction="row" gap="5px" alignItems="flex-end">
                <Typography sx={{ fontSize: "13px", fontWeight: "500" }}>{d}</Typography>
                <Typography sx={{ fontSize: "10px", color: "#6F747E" }}>{t}</Typography>
              </Stack>
            );
          },
        }
      ];
    }
    return columns;
  };

  return (
    <Box m="20px">
      <SearchActionComponent
        value={search}
        setState={setSearch}
        require={{ search: true, filter: true, download: true, sort: false }}
        downloadFile={handleDownload}
        toggleDrawer={toggleFilterDrawer}
        placeholder="Search booking ID, rider name, phone..."
      />

      <FilteredResults 
        options={filterOptions.map((opt) => 
          opt.filterName === "status" && (opt.state === "FAILED" || opt.state === "DELAYED") 
            ? { ...opt, state: "" } 
            : opt
        )} 
      />
      
      <CommonTableHead
        active={statusFilter === "FAILED" ? "Failed" : statusFilter === "DELAYED" ? "Delayed" : "All"}
        ButtonConfig={[
          { label: "All", value: "All", totalCount: (statusFilter !== "FAILED" && statusFilter !== "DELAYED") ? totalCount : "", onClick: () => setStatusFilter("") },
          { label: "Failed", value: "Failed", totalCount: statusFilter === "FAILED" ? totalCount : "", onClick: () => setStatusFilter("FAILED") },
          { label: "Delayed", value: "Delayed", totalCount: statusFilter === "DELAYED" ? totalCount : "", onClick: () => setStatusFilter("DELAYED") }
        ]}
        totalcount={totalCount}
      />
      
      <DataTable
        row={policies}
        columns={getColumns()}
        sx={{ cursor: "default" }}
        alternativeId="id"
        paginationInnerObject={false}
        paginationModel={paginationModel}
        loading={loading}
        setPaginationModel={setPaginationModel}
        totalCount={totalCount}
      />

      <SwipeableDrawerComponent
        isOpen={showFilter.right}
        anchor="right"
        onClose={toggleFilterDrawer("right", false)}
      >
        <CustomFilter
          filterOptions={filterOptions}
          toggleDrawer={() => toggleFilterDrawer("right", false)}
        />
      </SwipeableDrawerComponent>

      <InsuranceDetailModal
        open={detailOpen}
        policyId={selectedPolicy}
        onClose={() => setDetailOpen(false)}
      />
    </Box>
  );
};

export default Insurance;
